import { useState } from 'react'
import { motion } from 'framer-motion'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import {
    CheckCircle, AlertCircle, Flame, Calendar,
    Minus, Plus, MessageSquare, ChevronDown, ChevronUp
} from 'lucide-react'
import { toast } from 'sonner'

export function DisciplineTrackerView() {
    const [dailyReport, setDailyReport] = useState({
        totalHours: 6,
        physics: 2,
        chemistry: 2,
        biology: 2,
        rating: 7
    })
    const [showFeedbackDetails, setShowFeedbackDetails] = useState(false)

    const handleHoursChange = (field: 'totalHours' | 'physics' | 'chemistry' | 'biology', delta: number) => {
        setDailyReport(prev => ({
            ...prev,
            [field]: Math.max(0, Math.min(24, prev[field] + delta))
        }))
    }

    const handleSubmit = () => {
        const { totalHours, physics, chemistry, biology } = dailyReport

        if (totalHours === 0) {
            toast.error("Please enter total study hours")
            return
        }

        if (Math.abs(physics + chemistry + biology - totalHours) > 0.5) {
            toast.error(`Subject split (${physics + chemistry + biology}h) does not match Total Hours (${totalHours}h)`)
            return
        }

        toast.success("Discipline Report Submitted!")
    }

    const weeklyScores = [8, 7, 9, 6, 8, 9, 7]
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    const maxScore = 10

    return (
        <div className="space-y-4 md:space-y-6 pb-24 lg:pb-0">
            <h2 className="text-xl md:text-2xl font-bold text-theme-primary">Discipline Tracker</h2>

            <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
                {/* Left: Input Form */}
                <GlassmorphicCard className="p-4 md:p-6 lg:col-span-1 h-fit">
                    <h3 className="text-base md:text-lg font-bold text-theme-primary mb-4">Daily Report</h3>
                    <div className="space-y-5">
                        {/* Total Hours with Stepper */}
                        <div>
                            <label className="block text-sm font-medium text-theme-secondary mb-2">Total Study Hours</label>
                            <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
                                <button
                                    type="button"
                                    onClick={() => handleHoursChange('totalHours', -0.5)}
                                    className="w-10 h-10 rounded-lg bg-white dark:bg-slate-700 flex items-center justify-center text-theme-secondary hover:text-red-500 transition-colors shadow-sm"
                                >
                                    <Minus className="h-4 w-4" />
                                </button>
                                <div className="text-center">
                                    <span className="text-3xl font-bold text-theme-primary">{dailyReport.totalHours}</span>
                                    <span className="text-lg text-theme-secondary ml-1">hrs</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleHoursChange('totalHours', 0.5)}
                                    className="w-10 h-10 rounded-lg bg-white dark:bg-slate-700 flex items-center justify-center text-theme-secondary hover:text-green-500 transition-colors shadow-sm"
                                >
                                    <Plus className="h-4 w-4" />
                                </button>
                            </div>
                        </div>

                        {/* Subject Split */}
                        <div>
                            <label className="block text-sm font-medium text-theme-secondary mb-2">Subject Breakdown</label>
                            <div className="space-y-2">
                                {[
                                    { key: 'physics' as const, label: 'Physics', color: 'blue' },
                                    { key: 'chemistry' as const, label: 'Chemistry', color: 'green' },
                                    { key: 'biology' as const, label: 'Biology', color: 'purple' },
                                ].map(subject => (
                                    <div key={subject.key} className="flex items-center justify-between p-2.5 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                                        <span className={`text-sm font-medium text-${subject.color}-500`}>{subject.label}</span>
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={() => handleHoursChange(subject.key, -0.5)}
                                                className="w-7 h-7 rounded-md bg-white dark:bg-slate-700 flex items-center justify-center text-slate-400 hover:text-red-500 text-xs"
                                            >
                                                <Minus className="h-3 w-3" />
                                            </button>
                                            <span className="w-8 text-center font-bold text-theme-primary text-sm">{dailyReport[subject.key]}</span>
                                            <button
                                                type="button"
                                                onClick={() => handleHoursChange(subject.key, 0.5)}
                                                className="w-7 h-7 rounded-md bg-white dark:bg-slate-700 flex items-center justify-center text-slate-400 hover:text-green-500 text-xs"
                                            >
                                                <Plus className="h-3 w-3" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Self Rating */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-medium text-theme-secondary">Self Rating</label>
                                <span className="text-sm font-bold text-primary-blue">{dailyReport.rating}/10</span>
                            </div>
                            <input
                                type="range"
                                min="1"
                                max="10"
                                value={dailyReport.rating}
                                onChange={e => setDailyReport({ ...dailyReport, rating: parseInt(e.target.value) })}
                                className="w-full h-2 bg-gradient-to-r from-red-300 via-yellow-300 to-green-300 rounded-lg appearance-none cursor-pointer"
                            />
                            <div className="flex justify-between text-xs text-theme-secondary mt-1">
                                <span>Distracted</span>
                                <span>Focused</span>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={handleSubmit}
                            className="w-full py-3 bg-gradient-to-r from-primary-blue to-blue-600 text-white font-bold rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all"
                        >
                            Submit Report
                        </button>
                    </div>
                </GlassmorphicCard>

                {/* Right: Stats & Charts */}
                <div className="lg:col-span-2 space-y-4 md:space-y-6">
                    {/* Stat Cards Row */}
                    <div className="grid grid-cols-3 gap-3 md:gap-4">
                        {/* Streak Card - Enhanced */}
                        <GlassmorphicCard className="p-4 md:p-5 relative overflow-hidden">
                            <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/10 blur-xl" />
                            <div className="relative z-10 flex flex-col items-center text-center">
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ type: 'spring', stiffness: 200 }}
                                    className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mb-2 shadow-lg shadow-amber-500/30"
                                >
                                    <Flame className="h-6 w-6 md:h-7 md:w-7 text-white" />
                                </motion.div>
                                <motion.p
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="text-2xl md:text-3xl font-bold text-theme-primary"
                                >
                                    5
                                </motion.p>
                                <p className="text-xs text-theme-secondary">Day Streak</p>
                            </div>
                        </GlassmorphicCard>

                        <GlassmorphicCard className="p-4 md:p-5 flex flex-col items-center justify-center text-center">
                            <div className="p-2.5 bg-blue-100 dark:bg-blue-900/30 text-blue-500 rounded-xl mb-2">
                                <CheckCircle className="h-5 w-5 md:h-6 md:w-6" />
                            </div>
                            <p className="text-2xl md:text-3xl font-bold text-theme-primary">92%</p>
                            <p className="text-xs text-theme-secondary">Submission</p>
                        </GlassmorphicCard>

                        <GlassmorphicCard className="p-4 md:p-5 flex flex-col items-center justify-center text-center">
                            <div className="p-2.5 bg-red-100 dark:bg-red-900/30 text-red-500 rounded-xl mb-2">
                                <AlertCircle className="h-5 w-5 md:h-6 md:w-6" />
                            </div>
                            <p className="text-2xl md:text-3xl font-bold text-theme-primary">2</p>
                            <p className="text-xs text-theme-secondary">Missed</p>
                        </GlassmorphicCard>
                    </div>

                    {/* Weekly Graph - Enhanced */}
                    <GlassmorphicCard className="p-4 md:p-6">
                        <div className="flex items-center justify-between mb-4 md:mb-6">
                            <h3 className="font-bold text-theme-primary text-sm md:text-base">Weekly Discipline Score</h3>
                            <span className="text-xs text-theme-secondary bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded-full">This Week</span>
                        </div>
                        <div className="h-40 md:h-48 flex items-end gap-2 md:gap-3">
                            {weeklyScores.map((score, i) => {
                                const heightPercent = (score / maxScore) * 100
                                const barColor = score >= 8 ? 'from-green-400 to-green-500' : score >= 5 ? 'from-blue-400 to-blue-500' : 'from-amber-400 to-amber-500'

                                return (
                                    <div key={i} className="flex-1 flex flex-col items-center group">
                                        <div className="relative w-full flex justify-center h-32 md:h-40">
                                            {/* Y-axis reference lines */}
                                            <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                                                {[10, 5, 0].map(val => (
                                                    <div key={val} className="w-full border-b border-dashed border-slate-100 dark:border-slate-800" />
                                                ))}
                                            </div>

                                            {/* Bar */}
                                            <motion.div
                                                initial={{ height: 0 }}
                                                animate={{ height: `${heightPercent}%` }}
                                                transition={{ duration: 0.6, delay: i * 0.08 }}
                                                className={`w-full max-w-[32px] md:max-w-[40px] rounded-t-lg bg-gradient-to-t ${barColor} opacity-80 group-hover:opacity-100 transition-opacity shadow-md relative z-10`}
                                            />

                                            {/* Tooltip */}
                                            <div className="absolute -top-6 left-1/2 -translate-x-1/2 px-2 py-1 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs font-bold rounded opacity-0 group-hover:opacity-100 transition-opacity z-20 whitespace-nowrap">
                                                {score}/10
                                            </div>
                                        </div>
                                        <span className="text-[10px] md:text-xs text-theme-secondary font-medium mt-2">{days[i]}</span>
                                    </div>
                                )
                            })}
                        </div>
                    </GlassmorphicCard>

                    {/* Missed Days - Timeline Style */}
                    <GlassmorphicCard className="p-4 md:p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-bold text-theme-primary text-sm md:text-base">Recent Missed Days</h3>
                            <span className="text-xs text-red-500 font-medium px-2 py-1 bg-red-100 dark:bg-red-900/20 rounded-full">Risk: Low</span>
                        </div>
                        <div className="relative">
                            {/* Timeline line */}
                            <div className="absolute left-[18px] md:left-[22px] top-2 bottom-2 w-0.5 bg-slate-200 dark:bg-slate-700" />

                            <div className="space-y-3">
                                {[
                                    { date: 'Oct 24, 2024', day: 'Thu', reason: 'Not Submitted', daysAgo: 2 },
                                    { date: 'Oct 18, 2024', day: 'Fri', reason: 'Not Submitted', daysAgo: 8 }
                                ].map((missed, i) => (
                                    <div key={i} className="flex items-center gap-3 md:gap-4 pl-1">
                                        <div className="relative z-10 w-9 h-9 md:w-11 md:h-11 rounded-xl bg-red-100 dark:bg-red-900/30 flex flex-col items-center justify-center shrink-0">
                                            <Calendar className="h-3 w-3 md:h-4 md:w-4 text-red-500 mb-0.5" />
                                            <span className="text-[9px] md:text-[10px] font-bold text-red-600">{missed.day}</span>
                                        </div>
                                        <div className="flex-1 flex items-center justify-between p-2.5 md:p-3 bg-slate-50 dark:bg-slate-800/30 rounded-xl">
                                            <div>
                                                <p className="text-sm font-medium text-theme-primary">{missed.date}</p>
                                                <p className="text-xs text-theme-secondary">{missed.reason}</p>
                                            </div>
                                            <span className="text-[10px] text-slate-400 shrink-0">{missed.daysAgo}d ago</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </GlassmorphicCard>

                    {/* Mentor Feedback - Expandable */}
                    <GlassmorphicCard className="p-4 md:p-6 border-l-4 border-l-primary-blue">
                        <button
                            onClick={() => setShowFeedbackDetails(!showFeedbackDetails)}
                            className="w-full flex items-center justify-between"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-sm shadow-lg">
                                    DS
                                </div>
                                <div className="text-left">
                                    <h3 className="font-bold text-theme-primary text-sm md:text-base">Mentor Feedback</h3>
                                    <p className="text-xs text-theme-secondary">Oct 25, 2024 • Dr. Sarah</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="text-right hidden sm:block">
                                    <p className="text-xs text-theme-secondary">Approved</p>
                                    <p className="font-bold text-green-600">6.5h</p>
                                </div>
                                {showFeedbackDetails ? (
                                    <ChevronUp className="h-5 w-5 text-slate-400" />
                                ) : (
                                    <ChevronDown className="h-5 w-5 text-slate-400" />
                                )}
                            </div>
                        </button>

                        {showFeedbackDetails && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800"
                            >
                                <div className="grid grid-cols-2 gap-3 mb-4">
                                    <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl">
                                        <p className="text-xs text-theme-secondary mb-1">Reported Hours</p>
                                        <p className="text-lg font-bold text-theme-primary">7.0h</p>
                                    </div>
                                    <div className="p-3 bg-green-50 dark:bg-green-900/10 rounded-xl">
                                        <p className="text-xs text-theme-secondary mb-1">Approved Hours</p>
                                        <p className="text-lg font-bold text-green-600">6.5h</p>
                                    </div>
                                </div>

                                <div className="bg-amber-50 dark:bg-amber-900/10 p-3 md:p-4 rounded-xl border border-amber-100 dark:border-amber-900/30">
                                    <div className="flex items-center gap-2 mb-2">
                                        <MessageSquare className="h-4 w-4 text-amber-500" />
                                        <p className="text-xs font-bold text-amber-600 dark:text-amber-500">Mentor Remarks</p>
                                    </div>
                                    <p className="text-sm text-slate-700 dark:text-slate-300 italic leading-relaxed">
                                        "Good effort on Physics, but Biology hours seem inflated compared to the output. Please focus on quality over quantity tomorrow."
                                    </p>
                                </div>
                            </motion.div>
                        )}
                    </GlassmorphicCard>
                </div>
            </div>
        </div>
    )
}
